import {
  Component,
  ChangeDetectionStrategy,
  contentChildren,
  model,
  input,
  output,
  computed,
  signal,
  effect,
  HostListener
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { WizardStepDirective } from './wizard-step.directive';
import { WizardStepBarComponent, StepBarItem } from './wizard-step-bar.component';

@Component({
  selector: 'app-wizard-shell',
  imports: [NgTemplateOutlet, WizardStepBarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './wizard-shell.component.html',
  styleUrl: './wizard-shell.component.scss'
})
export class WizardShellComponent {
  /**
   * Current active step index (into the *visible* steps array).
   * Two-way bindable: `[(activeStepIndex)]="step"`.
   */
  readonly activeStepIndex = model(0);

  /**
   * When true, steps must be completed in order.
   * Users can still click back to previously visited steps.
   */
  readonly linear = input(true);

  /** Emits when the active step changes. */
  readonly stepChange = output<{ from: number; to: number }>();

  /** Emits when the user clicks the submit action on the last step. */
  readonly wizardSubmit = output<void>();

  // ─── Content Children ──────────────────────────────────
  private readonly allSteps = contentChildren(WizardStepDirective);

  /** Steps filtered by their visibility signal. */
  readonly visibleSteps = computed(() =>
    this.allSteps().filter(s => s.stepVisible()())
  );

  /** Highest step index the user has reached (enables "click back"). */
  readonly highestVisited = signal(0);

  /** Slide direction for content animation ('forward' | 'backward' | null). */
  readonly slideDirection = signal<'forward' | 'backward' | null>(null);

  /** The currently active step directive. */
  readonly activeStep = computed(() => this.visibleSteps()[this.activeStepIndex()] ?? null);

  /** Whether the current step is the last visible step. */
  readonly isLastStep = computed(() =>
    this.activeStepIndex() === this.visibleSteps().length - 1
  );

  /** Whether the current step is the first visible step. */
  readonly isFirstStep = computed(() => this.activeStepIndex() === 0);

  /** Whether navigation forward is allowed (step valid or non-linear). */
  readonly canGoNext = computed(() => {
    const step = this.activeStep();
    if (!step) return false;
    return !this.linear() || step.stepValid()();
  });

  /** Step bar items derived from visible steps + current state. */
  readonly stepBarItems = computed<StepBarItem[]>(() => {
    const active = this.activeStepIndex();
    const highest = this.highestVisited();
    const lin = this.linear();

    return this.visibleSteps().map((s, i) => {
      const isCompleted = i < active && s.stepValid()();
      const isActive = i === active;

      let clickable: boolean;
      if (isActive) {
        clickable = false; // already here
      } else if (lin) {
        clickable = i <= highest; // can go back to visited
      } else {
        clickable = true; // non-linear: all clickable
      }

      return {
        id: s.stepId(),
        label: s.stepLabel(),
        icon: s.stepIcon(),
        state: isActive ? 'active' : isCompleted ? 'completed' : 'upcoming',
        clickable
      };
    });
  });

  constructor() {
    // Track highest visited step
    effect(() => {
      const idx = this.activeStepIndex();
      if (idx > this.highestVisited()) {
        this.highestVisited.set(idx);
      }
    });
  }

  // ─── Navigation ──────────────────────────────────────────

  /** Navigate to the next visible step. */
  goNext(): void {
    if (this.isLastStep() || !this.canGoNext()) return;
    this.navigateTo(this.activeStepIndex() + 1, 'forward');
  }

  /** Navigate to the previous visible step. */
  goBack(): void {
    if (this.isFirstStep()) return;
    this.navigateTo(this.activeStepIndex() - 1, 'backward');
  }

  /** Navigate to a specific step index (from step bar click). */
  goToStep(index: number): void {
    const current = this.activeStepIndex();
    if (index === current) return;
    this.navigateTo(index, index > current ? 'forward' : 'backward');
  }

  /** Navigate to a step by its id string. */
  goToStepById(stepId: string): void {
    const idx = this.visibleSteps().findIndex(s => s.stepId() === stepId);
    if (idx >= 0) this.goToStep(idx);
  }

  private navigateTo(index: number, direction: 'forward' | 'backward'): void {
    const steps = this.visibleSteps();
    if (index < 0 || index >= steps.length) return;

    const from = this.activeStepIndex();
    this.slideDirection.set(direction);
    this.activeStepIndex.set(index);
    this.stepChange.emit({ from, to: index });

    // Clear animation class after transition completes
    setTimeout(() => this.slideDirection.set(null), 380);
  }

  // ─── Keyboard ──────────────────────────────────────────

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Only handle keyboard on the step bar area
    const target = event.target as HTMLElement;
    if (!target.closest('.wsb')) return;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextClickableStep(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.focusNextClickableStep(-1);
        break;
    }
  }

  private focusNextClickableStep(direction: 1 | -1): void {
    const items = this.stepBarItems();
    let idx = this.activeStepIndex() + direction;
    while (idx >= 0 && idx < items.length) {
      if (items[idx].clickable) {
        this.goToStep(idx);
        return;
      }
      idx += direction;
    }
  }
}
